import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchConversations, createConversation, fetchConversation,
  deleteConversation, pinConversation, renameConversation,
  sendMessage, rateMessageApi, addOptimisticUserMsg, removeOptimisticMsg, addAssistantMessage, clearChatError,
} from '../../store/slices/chatSlice';
import api from '../../store/axios';
import '../../global.css';
import ThemeToggle from '../../components/ThemeToggle';
import WordmarkLogo from '../../components/WordmarkLogo';

// ── Icons ─────────────────────────────────────────────────────────────────────
const IconPlus = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v8M8 12h8"/></svg>;
const IconDashboard = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>;
const IconFile = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>;
const IconX = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>;
const IconMenu = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M3 12h18M3 6h18M3 18h18"/></svg>;
const IconCopy = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>;
const IconThumbUp = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>;
const IconThumbDown = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/><path d="M17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"/></svg>;
const IconPin = ({ filled }) => <svg width="13" height="13" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const IconTrash = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2"/></svg>;
const IconEdit = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>;
const IconSearch = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>;

const SUGGESTED = [
  ['Translate', 'Translate this safety notice into Yoruba, Hausa, and Swahili with tone notes.'],
  ['Forecast', 'Forecast solar yield for our 220kW array in Kano over the next 72 hours.'],
  ['Triage', 'Here\'s a community safety alert in Kibera. Triage severity and route it.'],
  ['Analyze', 'Read this cooperative\'s books and summarize cashflow risk in plain Swahili.'],
];

// ── Logo ──────────────────────────────────────────────────────────────────────
function CybLogo({ size = 40, thinking = false }) {
  return (
    <div style={{ width: size, height: size, borderRadius: Math.round(size * 0.22), background: '#000', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative', overflow: 'hidden' }}>
      {thinking && <div className="logo-shimmer" />}
      <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 20 20" fill="none" style={{ position: 'relative', zIndex: 1 }}>
        <text x="1" y="16" fontFamily="-apple-system, BlinkMacSystemFont, sans-serif" fontSize="16" fontWeight="400" fill="#F4F4F4">c</text>
        <circle cx="17" cy="4" r="3" fill="#8B7CFF">
          {thinking && <animate attributeName="opacity" values="1;0.25;1" dur="1.2s" repeatCount="indefinite"/>}
        </circle>
      </svg>
    </div>
  );
}

// ── Stream animation ──────────────────────────────────────────────────────────
function Stream({ text, done }) {
  const [shown, setShown] = useState('');
  useEffect(() => {
    let i = 0;
    setShown('');
    const id = setInterval(() => {
      i += Math.max(2, Math.floor(Math.random() * 5));
      if (i >= text.length) { setShown(text); clearInterval(id); done?.(); }
      else setShown(text.slice(0, i));
    }, 14);
    return () => clearInterval(id);
  }, [text, done]);
  return <span style={{ whiteSpace: 'pre-wrap' }}>{shown}<span className="cursor" /></span>;
}

// ── Markdown ──────────────────────────────────────────────────────────────────
function CodeBlock({ lang, code }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  return (
    <div className="md-codeblock">
      <div className="md-codeblock-head">
        <span className="md-codelang">{lang || 'code'}</span>
        <button className="md-copybtn" onClick={copy}>{copied ? '✓ Copied' : 'Copy'}</button>
      </div>
      <pre className="md-code"><code>{code}</code></pre>
    </div>
  );
}

function parseInline(text, key) {
  const parts = text.split(/(`[^`\n]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) return <strong key={`${key}-b${i}`}>{p.slice(2, -2)}</strong>;
    if (p.startsWith('*') && p.endsWith('*')) return <em key={`${key}-e${i}`}>{p.slice(1, -1)}</em>;
    if (p.startsWith('`') && p.endsWith('`')) return <code key={`${key}-c${i}`} className="md-inline-code">{p.slice(1, -1)}</code>;
    return p;
  });
}

function MarkdownText({ text }) {
  const blocks = text.split(/(```[\s\S]*?```)/g);
  return (
    <div className="md-body">
      {blocks.map((block, bi) => {
        if (block.startsWith('```')) {
          const inner = block.slice(3, -3);
          const nl = inner.indexOf('\n');
          const lang = nl > -1 ? inner.slice(0, nl).trim() : '';
          const code = nl > -1 ? inner.slice(nl + 1).trimEnd() : inner.trimEnd();
          return <CodeBlock key={bi} lang={lang} code={code} />;
        }
        const lines = block.split('\n');
        const rendered = [];
        let listItems = [];
        const flushList = () => {
          if (listItems.length) { rendered.push(<ul key={`ul-${rendered.length}`} className="md-list">{listItems}</ul>); listItems = []; }
        };
        lines.forEach((line, li) => {
          if (/^[-*•]\s/.test(line)) { listItems.push(<li key={li}>{parseInline(line.replace(/^[-*•]\s/, ''), `${bi}-${li}`)}</li>); return; }
          flushList();
          if (line.startsWith('### ')) { rendered.push(<h4 key={li} className="md-h3">{parseInline(line.slice(4), `${bi}-${li}`)}</h4>); return; }
          if (line.startsWith('## ')) { rendered.push(<h3 key={li} className="md-h2">{parseInline(line.slice(3), `${bi}-${li}`)}</h3>); return; }
          if (line.startsWith('# ')) { rendered.push(<h2 key={li} className="md-h1">{parseInline(line.slice(2), `${bi}-${li}`)}</h2>); return; }
          if (line.trim() === '') { rendered.push(<div key={li} className="md-br" />); return; }
          rendered.push(<p key={li} className="md-p">{parseInline(line, `${bi}-${li}`)}</p>);
        });
        flushList();
        return <React.Fragment key={bi}>{rendered}</React.Fragment>;
      })}
    </div>
  );
}

// ── Message actions ───────────────────────────────────────────────────────────
function MessageActions({ msg, convId, dispatch }) {
  const [copied, setCopied] = useState(false);
  const copy = (e) => { e.stopPropagation(); navigator.clipboard?.writeText(msg.content); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  const rate = (r) => dispatch(rateMessageApi({ convId, msgId: msg._id, rating: msg.rating === r ? null : r }));
  return (
    <div className="msg-actions">
      <button className={`msg-action-btn${copied ? ' active' : ''}`} onClick={copy} title="Copy"><IconCopy /></button>
      <button className={`msg-action-btn${msg.rating === 'up' ? ' active' : ''}`} onClick={() => rate('up')} title="Good response"><IconThumbUp /></button>
      <button className={`msg-action-btn${msg.rating === 'down' ? ' active' : ''}`} onClick={() => rate('down')} title="Bad response"><IconThumbDown /></button>
    </div>
  );
}

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

// ── Conversation item in sidebar ──────────────────────────────────────────────
function ConvItem({ conv, active, onSelect, onPin, onDelete, onRename }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(conv.title);
  const menuRef = useRef();

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const submitRename = () => {
    if (renameVal.trim() && renameVal !== conv.title) onRename(renameVal.trim());
    setRenaming(false);
    setMenuOpen(false);
  };

  return (
    <div className={`conv-item${active ? ' active' : ''}`} onClick={() => !renaming && onSelect()}>
      {renaming ? (
        <input
          className="conv-rename-input"
          value={renameVal}
          autoFocus
          onChange={e => setRenameVal(e.target.value)}
          onBlur={submitRename}
          onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setRenaming(false); }}
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <>
          <div className="conv-item-title">
            {conv.pinned && <span className="conv-pin-dot" />}
            <span>{conv.title}</span>
          </div>
          <div className="conv-item-actions" ref={menuRef}>
            <button className="conv-action-btn" onClick={e => { e.stopPropagation(); setMenuOpen(o => !o); }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
            </button>
            {menuOpen && (
              <div className="conv-menu">
                <button onClick={e => { e.stopPropagation(); setRenaming(true); setMenuOpen(false); }}>
                  <IconEdit /> Rename
                </button>
                <button onClick={e => { e.stopPropagation(); onPin(); setMenuOpen(false); }}>
                  <IconPin filled={conv.pinned} /> {conv.pinned ? 'Unpin' : 'Pin'}
                </button>
                <button className="danger" onClick={e => { e.stopPropagation(); onDelete(); setMenuOpen(false); }}>
                  <IconTrash /> Delete
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function Chat() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { convId } = useParams();

  const { user } = useSelector(s => s.auth);
  const { conversations, messages: allMessages, loadingConvs, loadingMsg, error: chatError, listFetched } = useSelector(s => s.chat);
  const firstName = user?.first_name || user?.firstName || user?.name?.split(' ')[0] || null;

  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const scrollRef = useRef();
  const bottomRef = useRef();
  const textareaRef = useRef();
  const pendingRef = useRef(null); // { convId, result } waiting for stream to finish
  const abortRef = useRef(null);  // RTK dispatch promise — call .abort() to cancel

  const activeConv = conversations.find(c => c._id === convId);
  const messages = (convId && allMessages[convId]) || [];

  // Load conversation list once
  useEffect(() => {
    if (!listFetched) dispatch(fetchConversations());
  }, []); // eslint-disable-line

  // Load messages when convId changes
  useEffect(() => {
    if (convId && (!allMessages[convId])) {
      dispatch(fetchConversation(convId));
    }
  }, [convId]); // eslint-disable-line

  // Lock page scroll
  useEffect(() => {
    document.documentElement.classList.add('page-locked');
    return () => document.documentElement.classList.remove('page-locked');
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  // Scroll as stream text grows
  useEffect(() => {
    if (streaming) bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [streamText, streaming]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 180) + 'px';
  }, [draft]);

  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 800) setDrawerOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  // Debounced conversation search
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const id = setTimeout(async () => {
      setSearchBusy(true);
      try {
        const { data } = await api.get(`/conversations/search?q=${encodeURIComponent(searchQuery.trim())}`);
        setSearchResults(data.conversations || []);
      } catch { setSearchResults([]); }
      finally { setSearchBusy(false); }
    }, 350);
    return () => clearTimeout(id);
  }, [searchQuery]);

  const handleNewConversation = async () => {
    const res = await dispatch(createConversation()).unwrap();
    navigate(`/chat/${res._id}`);
    setDraft('');
    setDrawerOpen(false);
  };

  const handleSelectConv = (id) => {
    navigate(`/chat/${id}`);
    setDrawerOpen(false);
  };

  const handleDelete = async (id) => {
    await dispatch(deleteConversation(id));
    if (convId === id) navigate('/chat');
  };

  const handlePin = (id, pinned) => dispatch(pinConversation({ convId: id, pinned: !pinned }));

  const handleRename = (id, title) => dispatch(renameConversation({ convId: id, title }));

  const send = useCallback(async (text) => {
    if (!text.trim() || streaming) return;
    setDraft('');

    let targetId = convId;
    if (!targetId) {
      const res = await dispatch(createConversation()).unwrap();
      targetId = res._id;
      navigate(`/chat/${res._id}`, { replace: true });
    }

    // Optimistic user message
    const tempId = crypto.randomUUID?.() || Math.random().toString(36).slice(2);
    dispatch(addOptimisticUserMsg({ convId: targetId, text, tempId }));
    setStreaming(true);
    setStreamText('');
    pendingRef.current = null;

    try {
      const promise = dispatch(sendMessage({ convId: targetId, content: text }));
      abortRef.current = promise;
      const res = await promise.unwrap();
      if (res.result) {
        pendingRef.current = { convId: targetId, result: res.result };
        setStreamText(res.result);
      } else {
        dispatch(removeOptimisticMsg({ convId: targetId, tempId }));
        setStreaming(false);
      }
    } catch (err) {
      // __stopped__ means user clicked stop — keep the user message, just stop streaming
      if (err !== '__stopped__') {
        dispatch(removeOptimisticMsg({ convId: targetId, tempId }));
      }
      setStreaming(false);
    }
  }, [streaming, convId, dispatch, navigate]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(draft); }
  };

  const stopGeneration = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
    setStreamText('');
    pendingRef.current = null;
  }, []);

  const commitStream = useCallback(() => {
    const pending = pendingRef.current;
    if (pending?.result) dispatch(addAssistantMessage({ convId: pending.convId, content: pending.result }));
    pendingRef.current = null;
    setStreaming(false);
    setStreamText('');
  }, [dispatch]);

  const retryLast = () => {
    const lastUser = [...messages].reverse().find(m => m.role === 'user');
    if (lastUser) { dispatch(clearChatError()); send(lastUser.content); }
  };

  // Split conversations into pinned and recent
  const pinned = conversations.filter(c => c.pinned);
  const recent = conversations.filter(c => !c.pinned);

  return (
    <div className="chat-shell">
      {/* Mobile bar */}
      <div className="chat-mobile-bar">
        <button className="chat-drawer-btn" onClick={() => setDrawerOpen(o => !o)} aria-label="Open sidebar"><IconMenu /></button>
        <span className="chat-mobile-title">{firstName ? `Hi, ${firstName}` : 'CYB-1'}</span>
        <ThemeToggle />
      </div>

      {drawerOpen && <div className="chat-backdrop" onClick={() => setDrawerOpen(false)} style={{ zIndex: 40 }} />}

      {/* Sidebar */}
      <aside className={`chat-side${drawerOpen ? ' open' : ''}`}>
        <button className="chat-side-close" onClick={() => setDrawerOpen(false)} aria-label="Close"><IconX /></button>

        <div className="chat-side-top">
          <div style={{ paddingBottom: 12 }}><WordmarkLogo size={17} onClick={() => setDrawerOpen(false)} /></div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="new-chat-btn" style={{ flex: 1 }} onClick={handleNewConversation}><IconPlus />New conversation</button>
            <button
              className={`chat-search-btn${searchOpen ? ' active' : ''}`}
              onClick={() => { setSearchOpen(o => !o); setSearchQuery(''); setSearchResults([]); }}
              title="Search conversations"
            ><IconSearch /></button>
          </div>
          {searchOpen && (
            <div style={{ marginTop: 8 }}>
              <input
                className="conv-search-input"
                autoFocus
                placeholder="Search conversations…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
          )}
        </div>

        <div className="chat-side-convs">
          {searchOpen && searchQuery.trim() ? (
            <>
              {searchBusy && <div style={{ padding: '10px 0', fontSize: 12, color: 'var(--fg-dim)', textAlign: 'center' }}>Searching…</div>}
              {!searchBusy && searchResults.length === 0 && (
                <div style={{ padding: '16px 0', fontSize: 12, color: 'var(--fg-dim)', textAlign: 'center' }}>No results for "{searchQuery}"</div>
              )}
              {!searchBusy && searchResults.length > 0 && (
                <div className="conv-list">
                  {searchResults.map(c => (
                    <ConvItem key={c._id} conv={c} active={c._id === convId}
                      onSelect={() => { handleSelectConv(c._id); setSearchOpen(false); setSearchQuery(''); }}
                      onPin={() => handlePin(c._id, c.pinned)}
                      onDelete={() => handleDelete(c._id)}
                      onRename={(t) => handleRename(c._id, t)}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
          {loadingConvs && <div className="side-loading"><span /><span /><span /></div>}

          {pinned.length > 0 && (
            <>
              <div className="side-label">Pinned</div>
              <div className="conv-list">
                {pinned.map(c => (
                  <ConvItem key={c._id} conv={c} active={c._id === convId}
                    onSelect={() => handleSelectConv(c._id)}
                    onPin={() => handlePin(c._id, c.pinned)}
                    onDelete={() => handleDelete(c._id)}
                    onRename={(t) => handleRename(c._id, t)}
                  />
                ))}
              </div>
            </>
          )}

          {recent.length > 0 && (
            <>
              <div className="side-label">{pinned.length > 0 ? 'Recent' : 'Conversations'}</div>
              <div className="conv-list">
                {recent.map(c => (
                  <ConvItem key={c._id} conv={c} active={c._id === convId}
                    onSelect={() => handleSelectConv(c._id)}
                    onPin={() => handlePin(c._id, c.pinned)}
                    onDelete={() => handleDelete(c._id)}
                    onRename={(t) => handleRename(c._id, t)}
                  />
                ))}
              </div>
            </>
          )}

          {!loadingConvs && conversations.length === 0 && (
            <div style={{ padding: '20px 12px', fontSize: 12, color: 'var(--fg-faint)', textAlign: 'center', lineHeight: 1.6 }}>
              No conversations yet.<br />Start one above.
            </div>
          )}
            </>
          )}
        </div>

        <div className="chat-side-foot">
          <Link to="/dashboard" className="side-link" onClick={() => setDrawerOpen(false)}><IconDashboard />Dashboard</Link>
          <Link to="/docs" className="side-link" onClick={() => setDrawerOpen(false)}><IconFile />Docs</Link>
          <div className="side-foot-row">
            <div className="side-legal"><Link to="/legal">Privacy</Link><Link to="/terms">Terms</Link></div>
            <ThemeToggle />
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="chat-main">
        <header className="chat-top">
          <div>
            <div className="chat-top-title">{activeConv?.title || (convId ? 'Loading…' : 'New conversation')}</div>
            <div className="chat-top-meta" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>CYB-1 · {messages.length} message{messages.length !== 1 ? 's' : ''}</span>
              {messages.length >= 20 && (
                <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: 'rgba(255,160,0,0.12)', color: '#f0a000', border: '1px solid rgba(255,160,0,0.25)', fontFamily: 'var(--font-mono)', letterSpacing: '0.04em' }}>
                  Long context · start new chat to avoid drift
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <ThemeToggle />
            <Link to="/dashboard" className="btn btn-ghost" style={{ padding: '7px 14px', fontSize: 12 }}>API keys</Link>
            <Link to="/signup" className="btn btn-primary" style={{ padding: '7px 14px', fontSize: 12 }}>Upgrade</Link>
          </div>
        </header>

        <div className="chat-scroll" ref={scrollRef}>
          {!convId ? (
            /* Landing — no conversation selected */
            <div className="chat-blank">
              <CybLogo size={48} />
              <h1 className="chat-blank-title">
                {firstName ? <>Hey {firstName}, what's on your mind?</> : <>Hey — what can I help you with?</>}
              </h1>
              <p className="chat-blank-sub">Ask me anything — in Yoruba, Hausa, Swahili, English, or whatever feels right.</p>
              <div className="chat-suggest">
                {SUGGESTED.map(([tag, text]) => (
                  <button key={tag} className="suggest-card" onClick={() => send(text)}>
                    <span className="suggest-tag">{tag}</span>
                    <span className="suggest-text">{text}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : loadingMsg && messages.length === 0 ? (
            <div style={{ padding: '60px 0', display: 'flex', justifyContent: 'center' }}>
              <div className="thinking-dots"><span /><span /><span /></div>
            </div>
          ) : messages.length === 0 && !streaming ? (
            <div className="chat-blank">
              <CybLogo size={48} />
              <h1 className="chat-blank-title" style={{ fontSize: 'clamp(22px, 3vw, 34px)' }}>New conversation</h1>
              <p className="chat-blank-sub">Ask me anything to get started.</p>
              <div className="chat-suggest">
                {SUGGESTED.map(([tag, text]) => (
                  <button key={tag} className="suggest-card" onClick={() => send(text)}>
                    <span className="suggest-tag">{tag}</span>
                    <span className="suggest-text">{text}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="chat-thread">
              {messages.map((m, i) => (
                <div key={m._id || i} className={`msg msg-${m.role}`}>
                  {m.role === 'assistant' && <CybLogo size={26} />}
                  <div className="msg-content-wrap">
                    <div className="msg-body">
                      {m.role === 'assistant' ? (
                        <MarkdownText text={m.content} />
                      ) : (
                        <span style={{ whiteSpace: 'pre-wrap' }}>{m.content}</span>
                      )}
                    </div>
                    <div className="msg-meta-row">
                      {m.createdAt && <span className="msg-time">{formatTime(m.createdAt)}</span>}
                      {m.role === 'assistant' && !m._id?.startsWith('temp') && (
                        <MessageActions msg={m} convId={convId} dispatch={dispatch} />
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {streaming && (
                <div className="msg msg-assistant">
                  <CybLogo size={26} thinking={!streamText} />
                  <div className="msg-content-wrap">
                    <div className="msg-body">
                      {streamText
                        ? <Stream text={streamText} done={commitStream} />
                        : <div className="thinking-dots"><span /><span /><span /></div>
                      }
                    </div>
                  </div>
                </div>
              )}

              <div ref={bottomRef} style={{ height: 1 }} />

              {chatError && (
                <div className="msg msg-assistant" style={{ alignItems: 'flex-start' }}>
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 2v4M6 9v.5" stroke="#ff6b6b" strokeWidth="1.5" strokeLinecap="round"/><circle cx="6" cy="6" r="5.25" stroke="#ff6b6b" strokeWidth="1.2"/></svg>
                  </div>
                  <div className="msg-content-wrap">
                    <div className="msg-body" style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{ color: '#ff8080', fontSize: 13.5, lineHeight: 1.55, flex: 1 }}>{chatError}</span>
                      <button onClick={() => dispatch(clearChatError())} style={{ background: 'none', border: 'none', color: 'var(--fg-faint)', cursor: 'pointer', padding: 0, flexShrink: 0, marginTop: 2 }}>
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M9 3 3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                      </button>
                    </div>
                    <div className="msg-meta-row">
                      <button className="msg-retry-btn" onClick={retryLast}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
                        Try again
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="composer-wrap">
          <div className="composer">
            <textarea
              ref={textareaRef}
              className="composer-input"
              placeholder="Type anything — Yoruba, Hausa, Swahili, English, or whatever feels right…"
              value={draft}
              rows={1}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="composer-toolbar">
              <div className="composer-tools">
                <button className="ctool" title="Attach file">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M9 4 5 8a2 2 0 1 0 2.8 2.8L11 7.5a3.5 3.5 0 0 0-5-5L2.5 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                </button>
                <button className="ctool" title="Voice input">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="5" y="2" width="4" height="7" rx="2" stroke="currentColor" strokeWidth="1.2"/><path d="M3 7a4 4 0 0 0 8 0M7 11v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                </button>
                <div className="ctool-divider" />
                <button className="ctool ctool-text">
                  <svg width="6" height="6" viewBox="0 0 6 6" fill="none" style={{ marginRight: 5 }}><circle cx="3" cy="3" r="3" fill="#8B7CFF"/></svg>
                  CYB-1 · 200K
                </button>
              </div>
              {streaming ? (
                <button className="send send-stop" onClick={stopGeneration} title="Stop generating">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><rect width="10" height="10" rx="2"/></svg>
                  <span>Stop</span>
                </button>
              ) : (
                <button className="send" onClick={() => send(draft)} disabled={!draft.trim()}>
                  <span>Send</span>
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M2 5.5h7m0 0L5.5 2m3.5 3.5L5.5 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                </button>
              )}
            </div>
          </div>
          <div className="composer-foot">CYB-1 may make mistakes — verify important outputs before acting on them.</div>
        </div>
      </main>
    </div>
  );
}
